import {
  Alert,
  AlertTitle,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { DateRangeFilter } from '@/components/DateRangeFilter';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Karta } from '@/components/Karta';
import { PasSekcji } from '@/components/PasSekcji';
import { DataQualityBadge, ErrorState, LoadingState } from '@/components/states';
import { MiniMetryka, PlytaGlowna } from '@/components/PlytaGlowna';
import { PrzyciskDowodu, type Dowod } from '@/components/SzufladaDowodu';
import { BrakDostepuDoDostaw } from './BrakDostepuDoDostaw';
import { czyWidziDostawy } from '@/lib/auth/dostepZrodel';
import { MIN_DELIVERIES, PROBLEM_THRESHOLD_PCT, problemSchoolsFrom } from './problemSchools';
import {
  KLASYFIKACJA_DRIVE,
  OKNO_TOLERANCJI,
  PaskKlasyfikacjiZrodla,
  SWIEZOSC_DRIVE,
  ZRODLO_BY_SCHOOL,
  coSieNieLiczy,
  formatMinuty,
  formatPct,
  formatPunkty,
  trojpodzial,
  udzial,
} from './punktualnosc';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatCount, formatRange } from '@/lib/format';
import { czyPustyZbior, wartoscDoPokazania } from '@/lib/pustyZbior';
import { useDeliveryBySchool } from '@/lib/useAnalytics';
import { useDateRange } from '@/lib/useDateRange';
import type { DeliverySchoolRow } from '@/lib/api/drive';

/**
 * PUNKTUALNOŚĆ WG SZKÓŁ — migracja do języka „SYGNAŁ".
 *
 * Pytanie ekranu: KTÓRE SZKOŁY TRZEBA WZIĄĆ NA WARSZTAT — i przy których próba jest za mała,
 * żeby cokolwiek orzekać.
 *
 * Liczba-bohater to nie średnia sieci, tylko LICZBA SZKÓŁ POD PROGIEM. Średnia sieci wygląda
 * dobrze przy kilku bardzo złych placówkach i nie prowadzi do żadnego działania; lista pod
 * progiem prowadzi.
 *
 * Kolumna „na czas" nie jest tu całą prawdą o szkole — obok stoją dwa pozostałe wyniki, bo
 * placówka z dużym udziałem dostaw PRZED czasem ma realny problem, którego sam procent „na czas"
 * nie pokaże.
 */
export function DriveSchoolsPage() {
  const auth = useAuth();
  const [range] = useDateRange();
  const { data, isLoading, error } = useDeliveryBySchool();

  if (!czyWidziDostawy(auth)) return <BrakDostepuDoDostaw />;

  const windowLabel = formatRange(range.date_from, range.date_to);

  // Kolejność ustala Control Center, a nie przypadek odpowiedzi: rosnąco po udziale dostaw na
  // czas, szkoły bez policzonego udziału na końcu. Poprzedni ekran DEKLAROWAŁ takie sortowanie,
  // ale go nie wykonywał — deklaracja, której kod nie realizuje, jest gorsza niż jej brak.
  const szkoly: DeliverySchoolRow[] = [...(data?.schools ?? [])].sort((a, b) => {
    const av = a.current.on_time_pct;
    const bv = b.current.on_time_pct;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return av - bv;
  });

  const dostawyLacznie = szkoly.reduce((acc, r) => acc + r.current.deliveries, 0);
  const ziarno = { wierszy: dostawyLacznie, bytow: szkoly.length };
  const pusteZrodlo = czyPustyZbior(ziarno);
  const problemy = problemSchoolsFrom(szkoly);
  const malaProba = szkoly.filter((r) => r.current.deliveries > 0 && r.current.deliveries < MIN_DELIVERIES).length;
  const bezIdentyfikatora = szkoly.reduce((acc, r) => acc + r.without_tuszama_id, 0);
  const liczbaProblemow = wartoscDoPokazania(problemy.length, ziarno);

  const dowodListy: Dowod = {
    tytul: 'Szkoły poniżej progu punktualności',
    etykietaWartosci: 'Szkoły do zajęcia się w pierwszej kolejności',
    wartosc: liczbaProblemow === null ? null : formatCount(liczbaProblemow),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: ZRODLO_BY_SCHOOL,
    klasyfikacja: KLASYFIKACJA_DRIVE,
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: windowLabel,
    oknoPorownawcze: data?.range.comparison
      ? formatRange(data.range.comparison.date_from, data.range.comparison.date_to)
      : null,
    wyliczenie:
      `Szkoły, które mają w oknie co najmniej ${MIN_DELIVERIES} dostaw, mają policzony udział „na czas" ` +
      `i udział ten jest niższy niż ${PROBLEM_THRESHOLD_PCT}%. Progi są jawne w kodzie (problemSchools.ts) ` +
      'i takie same tu oraz na ekranie startowym.',
    szczegoly: [
      ['Szkoły w wyniku', formatCount(szkoly.length)],
      ['Rekordy dostaw łącznie', formatCount(dostawyLacznie)],
      ['Próg punktualności', `${PROBLEM_THRESHOLD_PCT}%`],
      ['Minimalna próba', `${MIN_DELIVERIES} dostaw`],
      ['Szkoły z próbą poniżej minimum', formatCount(malaProba)],
      ['Rekordy bez identyfikatora systemu głównego', formatCount(bezIdentyfikatora)],
    ],
    proba: `${formatCount(szkoly.length)} szkół · ${formatCount(dostawyLacznie)} rekordów dostaw`,
    wymaganeMinimum: `${MIN_DELIVERIES} dostaw na szkołę`,
    ograniczenia: [
      OKNO_TOLERANCJI,
      `Szkoły z próbą mniejszą niż ${MIN_DELIVERIES} dostaw są z tej listy WYŁĄCZONE — przy takiej próbie ` +
        'jedna wpadka daje wynik nieodróżnialny od przypadku.',
      'Szkoły oznaczone brakiem historii są wyłączone z oceny: nie mają czego porównać.',
      'Suma rekordów dostaw i liczba szkół pochodzą z wierszy tej samej odpowiedzi — nie z osobnego zapytania.',
      ...(pusteZrodlo
        ? [
            'ŹRÓDŁO ZWRÓCIŁO PUSTY ZBIÓR: zero szkół i zero rekordów dostaw w tym oknie. To NIE jest ' +
              'zmierzone zero szkół z problemem — nie ma czego oceniać.',
          ]
        : []),
    ],
    czegoNieWynika:
      'Z tej liczby nie wynika, że pozostałe szkoły są obsłużone dobrze: placówki z próbą poniżej minimum są ' +
      'z oceny wyłączone i mogą mieć gorszy wynik, którego przy tej próbie nie da się odróżnić od przypadku. ' +
      'Nie wynika z niej także, KTO odpowiada za wynik — zestawienie jest w podziale na szkoły, a nie na ' +
      'restauracje ani kierowców, więc wskazanie winnego na tej podstawie byłoby zgadywaniem.',
  };

  function dowodSzkoly(row: DeliverySchoolRow): Dowod {
    const podzial = trojpodzial(row.current);
    return {
      tytul: row.school_name ?? `Szkoła #${row.school_id}`,
      etykietaWartosci: 'Udział dostaw na czas w oknie',
      wartosc: row.current.on_time_pct === null ? null : formatPct(row.current.on_time_pct),
      podstawa: { time: 'DELIVERY_DATE' },
      zrodlo: ZRODLO_BY_SCHOOL,
      klasyfikacja: KLASYFIKACJA_DRIVE,
      swiezosc: SWIEZOSC_DRIVE,
      oknoBiezace: windowLabel,
      oknoPorownawcze: data?.range.comparison
        ? formatRange(data.range.comparison.date_from, data.range.comparison.date_to)
        : null,
      wyliczenie: 'Udział podaje źródło dla tej szkoły; obok — trzy wyniki policzone z liczników tego wiersza.',
      szczegoly: [
        ['Rekordy dostaw', formatCount(row.current.deliveries)],
        ['Na czas', `${formatCount(podzial.naCzas)} · ${formatPct(udzial(podzial.naCzas, podzial.dostawy))}`],
        [
          'Przed czasem',
          `${formatCount(podzial.przedCzasem)} · ${formatPct(udzial(podzial.przedCzasem, podzial.dostawy))}`,
        ],
        ['Spóźnione', `${formatCount(podzial.spoznione)} · ${formatPct(udzial(podzial.spoznione, podzial.dostawy))}`],
        [
          'Spóźnienia w kubełkach',
          `do 5 min ${formatCount(row.current.late_5)} · do 15 min ${formatCount(
            row.current.late_15,
          )} · ponad 15 min ${formatCount(row.current.late_over_15)}`,
        ],
        ['Bez klasyfikacji', formatCount(podzial.nieprzypisane)],
        ['Średnie odchylenie', formatMinuty(row.current.avg_delta_min) ?? 'brak danych'],
        ['Okno porównawcze — na czas', formatPct(row.previous.on_time_pct)],
        ['Zmiana', formatPunkty(row.on_time_delta_points)],
        ['Rekordy bez identyfikatora systemu głównego', formatCount(row.without_tuszama_id)],
        ['Miasto', row.city ?? 'brak w źródle'],
      ],
      proba: `${formatCount(row.current.deliveries)} rekordów dostaw w oknie`,
      wymaganeMinimum: `${MIN_DELIVERIES} dostaw, żeby szkoła weszła do oceny progowej`,
      jakoscDanych: row.data_quality_reason,
      ograniczenia: [OKNO_TOLERANCJI, ...coSieNieLiczy(row.current)],
      czegoNieWynika:
        'Wynik tej szkoły nie mówi, czyja to sprawa: wiersz nie zawiera ani restauracji, ani kierowcy, więc na ' +
        'jego podstawie nie da się wskazać, gdzie powstało opóźnienie. Nie mówi też, że dostawy przed czasem są ' +
        'w tej szkole w porządku — są policzone osobno właśnie dlatego, że jedzenie dowiezione przed otwarciem ' +
        'stołówki potrafi czekać.',
    };
  }

  return (
    <Box>
      <NaglowekEkranu
        tytul="Punktualność wg szkół"
        opis="Które szkoły trzeba wziąć na warsztat w pierwszej kolejności — i przy których próba jest za mała, żeby cokolwiek orzekać."
        akcja={<DateRangeFilter wariant="naglowek" comparison={data?.range.comparison} />}
      />

      <PaskKlasyfikacjiZrodla />

      {isLoading ? <LoadingState /> : null}
      {error ? <ErrorState error={error} /> : null}

      {!isLoading && !error ? (
        <Stack spacing={3} className="sygnal-kaskada">
          <PlytaGlowna
            etykieta={`Szkoły poniżej ${PROBLEM_THRESHOLD_PCT}% punktualności`}
            wartosc={liczbaProblemow === null ? null : formatCount(liczbaProblemow)}
            opis={
              <Typography variant="body2">
                {pusteZrodlo
                  ? 'źródło nie zwróciło żadnej szkoły ani żadnego rekordu dostawy w tym oknie — to nie jest zmierzone zero szkół z problemem'
                  : `szkoły z co najmniej ${MIN_DELIVERIES} dostawami, których udział „na czas" jest niższy niż próg operacyjny; placówki z mniejszą próbą są z oceny wyłączone`}
              </Typography>
            }
            zrodlo={ZRODLO_BY_SCHOOL}
            okno={`${windowLabel} · oś czasu: dzień dostawy`}
            dowod={dowodListy}
            boczne={
              <>
                <MiniMetryka
                  wartosc={pusteZrodlo ? null : formatCount(szkoly.length)}
                  etykieta="szkół w wyniku"
                />
                <MiniMetryka
                  wartosc={pusteZrodlo ? null : formatCount(dostawyLacznie)}
                  etykieta="rekordów dostaw"
                />
                <MiniMetryka wartosc={pusteZrodlo ? null : formatCount(malaProba)} etykieta="próba pod minimum" />
                <MiniMetryka
                  wartosc={pusteZrodlo ? null : formatCount(bezIdentyfikatora)}
                  etykieta="rekordów bez ID"
                />
              </>
            }
          />

          {szkoly.length === 0 ? (
            <Alert severity="warning" icon={false}>
              <AlertTitle>Brak dostaw w wybranym oknie</AlertTitle>
              <Typography variant="body2">
                Źródło nie zwróciło ani jednej szkoły dla okna {windowLabel}. Nie wiadomo, czy dowozów nie
                było, czy nie zostały zapisane — to stan źródła, a nie wynik operacyjny.
              </Typography>
            </Alert>
          ) : (
            <>
              <PasSekcji
                tytul="Szkoły wg punktualności"
                opis="Lista do działania: najpierw te, gdzie dowóz zawodzi najczęściej."
              />

              <Karta nadtytul="Jedna szkoła = jeden wiersz" tytul="Punktualność wg szkół">
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '86ch', mb: 1.5 }}>
                Kolejność ustala Control Center: rosnąco po udziale dostaw na czas, szkoły bez policzonego
                udziału na końcu. To jest lista do działania, nie ranking chwały. Kolumna „na czas" jest
                udziałem podanym przez źródło; „przed czasem" i „spóźnione" to liczby rekordów, a udział pod
                nimi liczy Control Center jako rekordy ÷ dostawy tej szkoły. Kolumna „bez ID" pokazuje
                rekordy, które nie wejdą do analizy łączonej ze sprzedażą.
              </Typography>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Punktualność wg szkół">
                  <TableHead>
                    <TableRow>
                      <TableCell>Szkoła</TableCell>
                      <TableCell>Miasto</TableCell>
                      <TableCell align="right">Dostawy</TableCell>
                      <TableCell align="right">Na czas</TableCell>
                      <TableCell align="right">Przed czasem</TableCell>
                      <TableCell align="right">Spóźnione</TableCell>
                      <TableCell align="right">Zmiana</TableCell>
                      <TableCell align="right">Śr. odchylenie</TableCell>
                      <TableCell align="right">Bez ID</TableCell>
                      <TableCell>Jakość</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {szkoly.map((row) => {
                      const podzial = trojpodzial(row.current);
                      const podProgiem =
                        row.current.on_time_pct !== null && row.current.on_time_pct < PROBLEM_THRESHOLD_PCT;
                      return (
                        <TableRow key={row.school_id} hover>
                          <TableCell>{row.school_name ?? `#${row.school_id}`}</TableCell>
                          <TableCell>{row.city ?? '—'}</TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatCount(row.current.deliveries)}
                          </TableCell>
                          <TableCell
                            align="right"
                            className="sygnal-liczba"
                            sx={{ fontWeight: 700, color: podProgiem ? 'var(--uwaga)' : 'inherit' }}
                          >
                            {formatPct(row.current.on_time_pct)}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatCount(podzial.przedCzasem)}
                            <Box component="span" sx={{ color: 'var(--tekst-slaby)', ml: 0.75, fontSize: 12 }}>
                              {formatPct(udzial(podzial.przedCzasem, podzial.dostawy))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatCount(podzial.spoznione)}
                            <Box component="span" sx={{ color: 'var(--tekst-slaby)', ml: 0.75, fontSize: 12 }}>
                              {formatPct(udzial(podzial.spoznione, podzial.dostawy))}
                            </Box>
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatPunkty(row.on_time_delta_points)}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {formatMinuty(row.current.avg_delta_min) ?? '—'}
                          </TableCell>
                          <TableCell align="right" className="sygnal-liczba">
                            {row.without_tuszama_id > 0 ? formatCount(row.without_tuszama_id) : '—'}
                          </TableCell>
                          <TableCell>
                            <DataQualityBadge quality={row.data_quality} reason={row.data_quality_reason} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                              <Link
                                to={`/drive/historia?from=${range.date_from}&to=${range.date_to}&school_id=${row.school_id}`}
                                style={{ fontSize: 12 }}
                              >
                                historia
                              </Link>
                              <PrzyciskDowodu dowod={dowodSzkoly(row)} etykieta="dowód" />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              </Karta>
            </>
          )}
        </Stack>
      ) : null}
    </Box>
  );
}
